import React from 'react';
import { Box, Typography, Grid, Card, CardContent, useTheme } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';

const CustomerTractionSlide: React.FC = () => {
  const theme = useTheme();
  const customers = [
    {
      name: 'eHomes',
      type: 'Southern California Brokerage',
      achievement: '5,000 → 100,000+ leads',
      description: 'Expanded from initial trial to entire lead database after remarkable results with AI assistant "Taylor"',
      impact: 'Leadership calls Oppy "a game changer" for lead follow-up',
      color: '#4CAF50',
      logo: '/logos/ehomes-logo.png'
    },
    {
      name: 'TheQwikFix',
      type: 'Service Professional',
      achievement: 'Early Adopter',
      description: 'Successfully deployed Oppy for customer engagement and lead management',
      impact: '24/7 response capability unlocked',
      color: '#2196F3',
      logo: 'https://logo.clearbit.com/theqwikfix.com'
    },
    {
      name: 'Arrival Home Loans',
      type: 'Mortgage Lender',
      achievement: 'Active Customer',
      description: 'Spreading awareness with their AI voice assistant for customer outreach',
      impact: 'Faster response times driving conversions',
      color: '#FF9800',
      logo: 'https://logo.clearbit.com/arrivalhomeloans.com'
    },
    {
      name: 'Orchard',
      type: 'Real Estate Platform',
      achievement: 'September 2025',
      description: 'Latest customer joining the growing Oppy ecosystem',
      impact: 'Expanding platform capabilities',
      color: '#9C27B0',
      logo: 'https://logo.clearbit.com/orchard.com'
    }
  ];

  const metrics = [
    { label: 'AI Response Time', value: '3 seconds', description: '9,000× faster than average agent' },
    { label: 'Conversion Increase', value: '2.5×', description: 'With Follow Up Boss integration' },
    { label: 'Lead Coverage', value: '100,000+', description: 'eHomes total leads managed by Oppy' }
  ];

  return (
    <Box
      sx={{
        color: theme.palette.text.primary,
        padding: 4,
        maxHeight: '100%',
        overflowY: 'auto'
      }}
    >
      <Typography variant="h3" align="center" sx={{ mb: 1 }}>
        Customer Traction & Impact
      </Typography>
      <Typography variant="subtitle1" align="center" sx={{ mb: 4, color: theme.palette.text.secondary }}>
        Real customers seeing real results — empowering the next wave of single-person powerhouses
      </Typography>

      {/* Key Metrics */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} justifyContent="center">
          {metrics.map((metric, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Card 
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', 
                  textAlign: 'center',
                  height: '100%'
                }}
              >
                <CardContent>
                  <Typography variant="h3" sx={{ color: theme.palette.success.main, fontWeight: 'bold', mb: 1 }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                    {metric.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {metric.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Customer Stories */}
      <Grid container spacing={3}>
        {customers.map((customer, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Card 
              sx={{ 
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                border: `2px solid ${customer.color}`,
                height: '100%'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {customer.logo ? (
                    <Box
                      component="img"
                      src={customer.logo}
                      alt={customer.name}
                      sx={{
                        height: 40,
                        width: 40,
                        mr: 2,
                        objectFit: 'contain',
                        backgroundColor: theme.palette.mode === 'dark' ? 'white' : theme.palette.grey[100],
                        borderRadius: '8px',
                        padding: '4px'
                      }}
                    />
                  ) : (
                    <BusinessIcon sx={{ color: customer.color, fontSize: 40, mr: 2 }} />
                  )}
                  <Box>
                    <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
                      {customer.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {customer.type}
                    </Typography>
                  </Box>
                </Box>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: customer.color, 
                    mb: 1,
                    fontWeight: 'bold'
                  }}
                >
                  {customer.achievement}
                </Typography>
                
                <Typography variant="body1" sx={{ color: theme.palette.text.primary, mb: 1 }}>
                  {customer.description}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <TrendingUpIcon sx={{ color: customer.color, mr: 1 }} />
                  <Typography variant="body2" sx={{ color: customer.color, fontStyle: 'italic' }}>
                    {customer.impact}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Bottom Stats */}
      <Box 
        sx={{ 
          mt: 4, 
          p: 3, 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        <PhoneInTalkIcon sx={{ fontSize: 48, color: theme.palette.success.main, mb: 2 }} />
        <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 1 }}>
          Strong Customer Funnel
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
          Growing adoption with full-time sales director onboard to accelerate customer closing
        </Typography>
      </Box>
    </Box>
  );
};

export default CustomerTractionSlide;
